import { ChangeEvent, useEffect, useState } from 'react';

import { Input, Select, Space } from 'antd';
import {
  composeMobilePhone,
  dialCodeForCountry,
  MOBILE_PHONE_COUNTRY_OPTIONS,
  nationalNumberMaxLength,
  normalizeMobilePhone,
  splitMobilePhone,
} from 'utils/mobilePhone';

export interface MobilePhoneInputProps {
  value?: string | null;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  status?: '' | 'error' | 'warning';
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * A country dropdown and a number field that together produce a single value in
 * international format, e.g. +61432123456.
 *
 * The number field takes digits only and is never reformatted as it is typed, so the
 * cursor stays where the user put it. The country dropdown supplies the dialling code
 * and the leading zero of a local number is dropped when the two are joined.
 */
export const MobilePhoneInput = ({
  value,
  onChange,
  onBlur,
  status,
  className = 'w-[302px]',
  disabled,
  placeholder = 'Mobile number',
}: MobilePhoneInputProps) => {
  const [country, setCountry] = useState(() => splitMobilePhone(value).country);
  const [nationalNumber, setNationalNumber] = useState(
    () => splitMobilePhone(value).nationalNumber
  );

  const dialCode = dialCodeForCountry(country);

  useEffect(() => {
    // Adopt a value set from outside the field — an existing record being edited, or
    // the result of an email lookup. Ignore the value this field itself produced,
    // otherwise a typed leading zero would be stripped back out mid-edit.
    if (normalizeMobilePhone(value) === composeMobilePhone(dialCode, nationalNumber)) return;

    const next = splitMobilePhone(value);
    setCountry(next.country);
    setNationalNumber(next.nationalNumber);
  }, [value]);

  const handleCountryChange = (nextCountry: string) => {
    setCountry(nextCountry);
    onChange?.(composeMobilePhone(dialCodeForCountry(nextCountry), nationalNumber));
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = (event.target.value || '')
      .replace(/\D/g, '')
      .slice(0, nationalNumberMaxLength(dialCode));

    setNationalNumber(digits);
    onChange?.(composeMobilePhone(dialCode, digits));
  };

  return (
    // Space.Compact joins the two controls into one bordered field, so the pair
    // reads as a single input and stays legible when the row wraps it onto its
    // own line.
    <Space.Compact className={className}>
      <Select
        // Collapsed to the country's two-letter code, since the dialling code sits in
        // the number field beside it. The dropdown still lists full country names, and
        // searches on them.
        className="h-10 w-[72px] shrink-0 font-medium"
        showSearch
        optionFilterProp="label"
        // The field is too narrow to show a country name, so let the dropdown size
        // itself to its content rather than to the field.
        popupMatchSelectWidth={false}
        options={MOBILE_PHONE_COUNTRY_OPTIONS}
        value={country}
        onChange={handleCountryChange}
        // Deliberately no onBlur: leaving the dropdown for the number box next to it
        // would otherwise mark the field touched, and the number would be judged
        // before the user had typed any of it.
        disabled={disabled}
        status={status}
        labelRender={(option) => String(option.value)}
      />
      <Input
        className="h-10 min-w-0 flex-1 font-medium"
        value={nationalNumber}
        onChange={handleNumberChange}
        onBlur={onBlur}
        status={status}
        disabled={disabled}
        placeholder={placeholder}
        inputMode="numeric"
        autoComplete="tel-national"
        // Decoration only — the dialling code is joined to the number on change, so
        // it must not become part of what the user is editing.
        prefix={<span className="text-gray-500">{dialCode}</span>}
      />
    </Space.Compact>
  );
};

export default MobilePhoneInput;
